import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import FinDataGrid from "../components/findatagrid";
import { readErrorMessage } from "./adminAiProviders";
import { tokens } from "../theme/theme";

const API_BASE = process.env.REACT_APP_API_URI;

const formatDateTime = (value) => (value ? new Date(value).toLocaleString("de-DE") : "");

const isActive = (row) => (row.status || "active") === "active";

const providerOf = (row) => row.llmprovider || row.llmProvider;

const stopGrid = (event) => event.stopPropagation();

function describeSource(source) {
  if (!source) return "";
  if (source.kind === "parameter") return `Parameter ${source.key}`;
  if (source.kind === "env") return `Env ${source.key}`;
  if (source.kind === "default") return "Code-Default";
  return "nicht gesetzt";
}

function testGlyph(ok, at) {
  if (ok === true) return "✓";
  if (ok === false) return "✗";
  return at ? "?" : "–";
}

function describeTest(test) {
  if (!test) return "Modell nicht in der Modellliste";
  if (!test.at) return "noch nicht getestet";
  const state = test.ok === true ? "ok" : test.ok === false ? "rot" : "unklar";
  const latency = test.latency_ms != null ? `, ${test.latency_ms} ms` : "";
  return `${state}${latency}, ${formatDateTime(test.at)}${test.message ? `: ${test.message}` : ""}`;
}

async function sendJson(path, method, body) {
  const response = await fetch(API_BASE + path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const data = await response.json().catch(() => null);
    const error = new Error(data?.message || `Fehler ${response.status}`);
    error.code = data?.code;
    throw error;
  }
  return response.json();
}

// Aktive Modelle nach Provider gruppiert, mit Teststatus. Select braucht flache Kinder, daher kein Fragment.
function modelMenuItems(models, providers) {
  const items = [];
  const active = models.filter(isActive);
  const providerRows = providers.filter((p) => p.status === "active");
  for (const provider of providerRows) {
    const ofProvider = active.filter((m) => {
      const name = providerOf(m);
      return name === provider.name || (provider.name === "llmstudio" && name === "lmstudio");
    });
    if (ofProvider.length === 0) continue;
    items.push(
      <ListSubheader key={`group-${provider.name}`}>
        {provider.label || provider.name} ({provider.is_local ? "lokal" : "extern"})
      </ListSubheader>,
    );
    for (const m of ofProvider) {
      items.push(
        <MenuItem key={m.model} value={m.model}>
          {testGlyph(m.last_test_ok, m.last_test_at)} {m.model}
        </MenuItem>,
      );
    }
  }
  return items;
}

function ModelSelect({ value, models, providers, disabled, onChange, label, size }) {
  const known = models.find((m) => m.model === value);
  const showExtra = value && !(known && isActive(known));
  const hint = known ? "archiviert" : "nicht in der Modellliste";
  return (
    <FormControl fullWidth variant="standard" size={size} disabled={disabled}>
      {label && <InputLabel>{label}</InputLabel>}
      <Select value={value || ""} onChange={(e) => onChange(e.target.value)} displayEmpty={!label}>
        {!value && !label && <MenuItem value=""><em>nicht gesetzt</em></MenuItem>}
        {showExtra && <MenuItem value={value}>{value} ({hint})</MenuItem>}
        {modelMenuItems(models, providers)}
      </Select>
    </FormControl>
  );
}

const AdminAiTasks = ({ models, providers, refreshKey, onChanged }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [tasks, setTasks] = useState([]);
  const [changes, setChanges] = useState([]);
  const [saving, setSaving] = useState(null);
  const [notice, setNotice] = useState(null);

  // { title, text, run } für jede Aktion, die Positionsdaten an einen externen Provider schickt.
  const [confirm, setConfirm] = useState(null);

  const [probeTask, setProbeTask] = useState(null);
  const [probeModel, setProbeModel] = useState("");
  const [probeWithPortfolio, setProbeWithPortfolio] = useState(false);
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState(null);
  const [probeError, setProbeError] = useState(null);

  const [advancedTask, setAdvancedTask] = useState(null);
  const [advancedValues, setAdvancedValues] = useState({ type: "", securities: "" });
  const [advancedError, setAdvancedError] = useState(null);

  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceFrom, setReplaceFrom] = useState("");
  const [replaceTo, setReplaceTo] = useState("");
  const [replacePreview, setReplacePreview] = useState(null);
  const [replaceConfirmed, setReplaceConfirmed] = useState(false);
  const [replaceBusy, setReplaceBusy] = useState(false);
  const [replaceError, setReplaceError] = useState(null);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ai_tasks`);
      if (!response.ok) {
        setNotice({ severity: "error", text: await readErrorMessage(response) });
        return;
      }
      setTasks(await response.json());
    } catch (error) {
      setNotice({ severity: "error", text: error.message });
    }
  }, []);

  const fetchChanges = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/ai_task_changes`);
      if (!response.ok) {
        setNotice({ severity: "error", text: await readErrorMessage(response) });
        return;
      }
      setChanges(await response.json());
    } catch (error) {
      setNotice({ severity: "error", text: error.message });
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchChanges();
  }, [fetchTasks, fetchChanges, refreshKey]);

  const afterWrite = (text) => {
    setNotice({ severity: "success", text });
    fetchTasks();
    fetchChanges();
    if (onChanged) onChanged();
  };

  const providerRowOfModel = (modelName) => {
    const row = models.find((m) => m.model === modelName);
    if (!row) return null;
    const name = providerOf(row) === "lmstudio" ? "llmstudio" : providerOf(row);
    return providers.find((p) => p.name === name) || null;
  };

  const saveModel = async (task, model, confirmExternal) => {
    setSaving(task.key);
    setNotice(null);
    try {
      const result = await sendJson(`/api/ai_tasks/${task.key}`, "PUT", { model, confirmExternal });
      afterWrite(result.changed
        ? `${task.label}: ${model} gilt ab dem nächsten Auslösen.`
        : `${task.label}: ${model} war bereits gesetzt.`);
    } catch (error) {
      if (error.code === "confirm_external") {
        askConfirm(task.label, model, error.message, () => saveModel(task, model, true));
      } else {
        setNotice({ severity: "error", text: `${task.label}: ${error.message}` });
      }
      fetchTasks();
    } finally {
      setSaving(null);
    }
  };

  const askConfirm = (label, target, text, run) => {
    setConfirm({ title: `${label}: externer Provider`, target, text, run });
  };

  const handleModelChange = (task, model) => {
    const provider = providerRowOfModel(model);
    if (task.sends_portfolio_data && provider && !provider.is_local) {
      askConfirm(
        task.label,
        model,
        `Der Prompt enthält Positionsdaten aus dem Portfolio: Bestand, Einstand, Gewinn und Gewichte. Sie gehen dann an ${provider.label || provider.name}.`,
        () => saveModel(task, model, true),
      );
      return;
    }
    saveModel(task, model);
  };

  // --- Testlauf ---
  const openProbe = (task) => {
    setProbeTask(task);
    setProbeModel(task.model || "");
    setProbeWithPortfolio(false);
    setProbeResult(null);
    setProbeError(null);
  };

  const probeProvider = providerRowOfModel(probeModel);
  const probeIsExternal = probeTask?.sends_portfolio_data && probeProvider && !probeProvider.is_local;

  const runProbe = async () => {
    setProbing(true);
    setProbeResult(null);
    setProbeError(null);
    try {
      setProbeResult(await sendJson(`/api/ai_tasks/${probeTask.key}/probe`, "POST", {
        model: probeModel || undefined,
        confirmExternal: probeWithPortfolio || undefined,
      }));
    } catch (error) {
      setProbeError(error.message);
    } finally {
      setProbing(false);
    }
  };

  // --- Erweitert ---
  const openAdvanced = (task) => {
    setAdvancedTask(task);
    setAdvancedValues({ type: task.type || "", securities: task.securities || "" });
    setAdvancedError(null);
  };

  const saveAdvanced = async () => {
    const body = {};
    if (advancedValues.type && advancedValues.type !== advancedTask.type) body.type = advancedValues.type;
    if (advancedValues.securities && advancedValues.securities !== advancedTask.securities) body.securities = advancedValues.securities;
    if (Object.keys(body).length === 0) {
      setAdvancedTask(null);
      return;
    }
    setAdvancedError(null);
    try {
      await sendJson(`/api/ai_tasks/${advancedTask.key}/advanced`, "PUT", body);
      setAdvancedTask(null);
      afterWrite(`${advancedTask.label}: Einstellungen gespeichert, gültig ab dem nächsten Auslösen.`);
    } catch (error) {
      setAdvancedError(error.message);
    }
  };

  // --- Sammelaktion ---
  const usedModels = [...new Set(tasks.map((task) => task.model).filter(Boolean))].sort();

  const openReplace = () => {
    setReplaceOpen(true);
    setReplaceFrom("");
    setReplaceTo("");
    setReplacePreview(null);
    setReplaceConfirmed(false);
    setReplaceError(null);
  };

  const previewReplace = async () => {
    setReplaceBusy(true);
    setReplaceError(null);
    setReplacePreview(null);
    setReplaceConfirmed(false);
    try {
      setReplacePreview(await sendJson("/api/ai_tasks/replace", "POST", { from: replaceFrom, to: replaceTo, dryRun: true }));
    } catch (error) {
      setReplaceError(error.message);
    } finally {
      setReplaceBusy(false);
    }
  };

  const executeReplace = async () => {
    setReplaceBusy(true);
    setReplaceError(null);
    try {
      const result = await sendJson("/api/ai_tasks/replace", "POST", {
        from: replaceFrom,
        to: replaceTo,
        confirmExternal: replaceConfirmed || undefined,
      });
      setReplaceOpen(false);
      afterWrite(`${replaceFrom} durch ${replaceTo} ersetzt: ${result.changes.length} Aufgaben, gültig ab dem nächsten Auslösen.`);
    } catch (error) {
      setReplaceError(error.message);
    } finally {
      setReplaceBusy(false);
    }
  };

  // --- Verlauf ---
  const revert = async (change, confirmExternal) => {
    setNotice(null);
    try {
      await sendJson(`/api/ai_task_changes/${change.id}/revert`, "POST", { confirmExternal });
      afterWrite(`${change.task_label}: zurückgenommen.`);
    } catch (error) {
      if (error.code === "confirm_external") {
        askConfirm(change.task_label, change.old_model, error.message, () => revert(change, true));
      } else {
        setNotice({ severity: "error", text: `${change.task_label}: ${error.message}` });
      }
    }
  };

  const describeValue = (model, provider) => (model ? `${model} (${provider || "?"})` : "nicht gesetzt");

  const taskColumns = [
    {
      field: "label",
      headerName: "Aufgabe",
      width: 210,
      cellClassName: "name-column--cell",
      renderCell: (params) => (
        <Tooltip title={[params.row.job ? `Job: ${params.row.job.name}${params.row.job.active ? "" : " (inaktiv)"}` : "Kein Job gefunden", params.row.portfolio_note].filter(Boolean).join(". ")}>
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <span>{params.value}</span>
            {params.row.sends_portfolio_data && <Chip label="Portfolio" size="small" color="warning" variant="outlined" />}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "model",
      headerName: "Modell",
      width: 270,
      sortable: false,
      // Das Raster darf Maus und Tastatur hier nicht abgreifen: Sein Zell-Fokus schließt das Menü sofort wieder.
      renderCell: (params) => (
        <Box width="100%" onMouseDown={stopGrid} onClick={stopGrid} onKeyDown={stopGrid}>
          <ModelSelect
            value={params.row.model}
            models={models}
            providers={providers}
            disabled={!params.row.job || saving === params.row.key}
            onChange={(model) => handleModelChange(params.row, model)}
          />
        </Box>
      ),
    },
    {
      field: "provider",
      headerName: "Provider",
      width: 100,
      renderCell: (params) => (
        <Tooltip title={params.row.provider_mismatch ? "Der Job nutzt einen anderen Provider, als das Modell heute zugeordnet ist. Modell neu wählen." : describeSource(params.row.provider_source)}>
          <span>{params.value}{params.row.provider_mismatch ? " ⚠" : ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "is_local",
      headerName: "Ort",
      width: 70,
      valueGetter: (params) => (params.row.is_local === null ? "?" : params.row.is_local ? "lokal" : "extern"),
    },
    {
      field: "source",
      headerName: "Herkunft",
      width: 195,
      valueGetter: (params) => describeSource(params.row.model_source),
    },
    {
      field: "test",
      headerName: "Test",
      width: 60,
      valueGetter: (params) => testGlyph(params.row.test?.ok, params.row.test?.at),
      renderCell: (params) => (
        <Tooltip title={describeTest(params.row.test)}><span>{params.value}</span></Tooltip>
      ),
    },
    {
      field: "lastRun",
      headerName: "Letzter Lauf",
      width: 145,
      valueGetter: (params) => formatDateTime(params.row.last_run?.end || params.row.last_run?.start),
    },
    {
      field: "lastMsg",
      headerName: "last_msg",
      width: 220,
      valueGetter: (params) => params.row.last_run?.msg || "",
      renderCell: (params) => (
        <Tooltip title={[params.value, params.row.last_run?.note].filter(Boolean).join(" · ")}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 190,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Button color="inherit" size="small" disabled={!params.row.job} onClick={() => openProbe(params.row)}>Testlauf</Button>
          {params.row.advanced && (
            <Button color="inherit" size="small" onClick={() => openAdvanced(params.row)}>Erweitert</Button>
          )}
        </Box>
      ),
    },
  ];

  const changeColumns = [
    {
      field: "changed_at",
      headerName: "Zeit",
      width: 170,
      valueGetter: (params) => formatDateTime(params.value),
    },
    { field: "changed_by", headerName: "Nutzer", width: 200 },
    { field: "task_label", headerName: "Aufgabe", width: 210 },
    {
      field: "action",
      headerName: "Art",
      width: 110,
      valueGetter: (params) => ({ set: "gesetzt", replace: "ersetzt", revert: "zurück" }[params.value] || params.value),
    },
    { field: "old", headerName: "Alt", width: 280, valueGetter: (params) => describeValue(params.row.old_model, params.row.old_provider) },
    { field: "new", headerName: "Neu", width: 280, valueGetter: (params) => describeValue(params.row.new_model, params.row.new_provider) },
    {
      field: "revertBtn",
      headerName: "",
      width: 130,
      sortable: false,
      renderCell: (params) => (params.row.can_revert ? (
        <Button color="inherit" size="small" onClick={() => revert(params.row)}>Rückgängig</Button>
      ) : null),
    },
  ];

  const canPreview = replaceFrom && replaceTo && replaceFrom !== replaceTo && !replaceBusy;
  const canExecute = replacePreview && replacePreview.affected.length > 0
    && (!replacePreview.requires_confirm || replaceConfirmed) && !replaceBusy;

  return (
    <>
      <Box mt={4} mb={1} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">AI-Aufgaben</Typography>
        <Button variant="outlined" color="secondary" onClick={openReplace}>Modell überall ersetzen</Button>
      </Box>
      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 1 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}
      <FinDataGrid
        id="gridAiTasks"
        height="740px"
        rows={tasks}
        columns={taskColumns}
        getRowId={(row) => row.key}
        getRowClassName={(params) => (params.row.alert ? "task-row--alert" : "")}
        disableSelectionOnClick
        sx={{
          typography: "body1",
          "& .task-row--alert": { backgroundColor: `${colors.redAccent[700]}66` },
          "& .task-row--alert:hover": { backgroundColor: `${colors.redAccent[700]}99` },
        }}
      />
      <Alert severity="info" sx={{ mt: 2 }}>
        Modell, Provider, Prompt-Typ und Securities gelten ohne Neustart ab dem nächsten Auslösen, der Ampel-Batch ab dem nächsten Zyklus. Laufende Läufe werden nicht unterbrochen. Einen Neustart von fin-services brauchen weiterhin: Schedule, aktiv/inaktiv, neue Jobs und Env-Variablen.
      </Alert>

      <Box mt={4} mb={1}>
        <Typography variant="h5">Verlauf</Typography>
      </Box>
      <FinDataGrid
        id="gridAiTaskChanges"
        height="30vh"
        density="compact"
        rows={changes}
        columns={changeColumns}
        disableSelectionOnClick
      />

      {/* Bestätigung: Positionsdaten an externen Provider */}
      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirm?.text}</DialogContentText>
          {confirm?.target && <DialogContentText sx={{ mt: 1 }}>Modell: {confirm.target}</DialogContentText>}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirm(null)}>Abbrechen</Button>
          <Button
            color="warning"
            onClick={() => {
              const { run } = confirm;
              setConfirm(null);
              run();
            }}
          >
            An externen Provider senden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Testlauf */}
      <Dialog open={Boolean(probeTask)} onClose={() => setProbeTask(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Testlauf: {probeTask?.label}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Prompt-Typ {probeTask?.prompt_type}. Nichts wird gespeichert und nichts gewechselt. Externe Provider berechnen echte Token.
          </DialogContentText>
          <ModelSelect label="Modell" value={probeModel} models={models} providers={providers} onChange={(m) => { setProbeModel(m); setProbeWithPortfolio(false); setProbeResult(null); }} />
          {probeIsExternal && (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Checkbox checked={probeWithPortfolio} onChange={(e) => setProbeWithPortfolio(e.target.checked)} />}
              label="Echte Positionsdaten mitsenden. Ohne Haken läuft der Prompt mit einem Papier außerhalb des Portfolios und ohne Positionsdaten."
            />
          )}
          {probeError && <Alert severity="error" sx={{ mt: 1 }}>{probeError}</Alert>}
          {probeResult && (
            <Alert severity={probeResult.ok ? "success" : "error"} sx={{ mt: 1 }}>
              {probeResult.model}, {probeResult.identifier}, {probeResult.type}: {probeResult.latency_ms} ms, {probeResult.length} Zeichen. {probeResult.message}
              {probeResult.portfolio_content === false ? " Ohne Positionsdaten." : ""}
              {probeResult.portfolio_content === true ? " Mit Positionsdaten." : ""}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setProbeTask(null)}>Schließen</Button>
          <Button color="inherit" disabled={probing || !probeModel} onClick={runProbe}>
            {probing ? <CircularProgress size={20} /> : "Testlauf starten"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Erweitert */}
      <Dialog open={Boolean(advancedTask)} onClose={() => setAdvancedTask(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Erweitert: {advancedTask?.label}</DialogTitle>
        <DialogContent>
          {advancedError && <Alert severity="error" sx={{ mb: 1 }}>{advancedError}</Alert>}
          {advancedTask?.advanced && (
            <>
              <FormControl fullWidth variant="standard" margin="dense">
                <InputLabel>Prompt-Typ</InputLabel>
                <Select value={advancedValues.type} onChange={(e) => setAdvancedValues({ ...advancedValues, type: e.target.value })}>
                  {[...new Set([advancedTask.type, ...advancedTask.advanced.types].filter(Boolean))].map((value) => (
                    <MenuItem key={value} value={value}>{value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth variant="standard" margin="dense">
                <InputLabel>Securities</InputLabel>
                <Select value={advancedValues.securities} onChange={(e) => setAdvancedValues({ ...advancedValues, securities: e.target.value })}>
                  {[...new Set([advancedTask.securities, ...advancedTask.advanced.securities].filter(Boolean))].map((value) => (
                    <MenuItem key={value} value={value}>{value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setAdvancedTask(null)}>Abbrechen</Button>
          <Button color="inherit" onClick={saveAdvanced}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Modell überall ersetzen */}
      <Dialog open={replaceOpen} onClose={() => setReplaceOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Modell überall ersetzen</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={2} alignItems="flex-end">
            <FormControl fullWidth variant="standard" margin="dense">
              <InputLabel>Altes Modell</InputLabel>
              <Select value={replaceFrom} onChange={(e) => { setReplaceFrom(e.target.value); setReplacePreview(null); }}>
                {usedModels.map((model) => <MenuItem key={model} value={model}>{model}</MenuItem>)}
              </Select>
            </FormControl>
            <ModelSelect label="Neues Modell" value={replaceTo} models={models} providers={providers} onChange={(m) => { setReplaceTo(m); setReplacePreview(null); }} />
            <Button variant="outlined" color="secondary" sx={{ flexShrink: 0 }} disabled={!canPreview} onClick={previewReplace}>Vorschau</Button>
          </Box>
          {replaceError && <Alert severity="error" sx={{ mt: 2 }}>{replaceError}</Alert>}
          {replacePreview && replacePreview.affected.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>Keine Aufgabe nutzt dieses Modell.</Alert>
          )}
          {replacePreview && replacePreview.affected.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2">
                {replacePreview.affected.length} Aufgaben wechseln zu {replacePreview.to} ({replacePreview.provider}, {replacePreview.is_local ? "lokal" : "extern"}):
              </Typography>
              <ul>
                {replacePreview.affected.map((row) => (
                  <li key={row.key}>
                    {row.label}, heute {row.old_provider} aus {describeSource(row.source)}
                    {row.job_active ? "" : ", Job inaktiv"}
                    {row.needs_confirm ? ", enthält Positionsdaten" : ""}
                  </li>
                ))}
              </ul>
              {replacePreview.requires_confirm && (
                <FormControlLabel
                  control={<Checkbox checked={replaceConfirmed} onChange={(e) => setReplaceConfirmed(e.target.checked)} />}
                  label={`Positionsdaten aus dem Portfolio dürfen an ${replacePreview.provider} gehen.`}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setReplaceOpen(false)}>Abbrechen</Button>
          <Button color="warning" disabled={!canExecute} onClick={executeReplace}>
            {replaceBusy ? <CircularProgress size={20} /> : "Ersetzen"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminAiTasks;
